import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { formatYear } from "@hunch/core";
import { AdminApiError, adminApi } from "../api.js";
import styles from "../admin.module.css";

interface Category {
  id: number;
  slug: string;
  nameDe: string;
}

interface EventDto {
  id: number;
  year: number;
  fame: 1 | 2 | 3;
  textDe: string;
  textEn: string;
  infoDe: string | null;
  infoEn: string | null;
  sourceLabel: string;
  sourceUrl: string;
  status: "draft" | "published";
  note: string | null;
  categoryIds: number[];
}

type FormState = Omit<EventDto, "id">;

const EMPTY_FORM: FormState = {
  year: new Date().getFullYear(),
  fame: 2,
  textDe: "",
  textEn: "",
  infoDe: "",
  infoEn: "",
  sourceLabel: "",
  sourceUrl: "",
  status: "draft",
  note: "",
  categoryIds: [],
};

export function EventForm() {
  const { id } = useParams();
  const isNew = id === undefined;
  const navigate = useNavigate();

  const [categories, setCategories] = useState<Category[]>([]);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<string[]>([]);
  const [loaded, setLoaded] = useState(isNew);

  useEffect(() => {
    adminApi.get<Category[]>("/api/admin/categories").then(setCategories);
  }, []);

  useEffect(() => {
    if (isNew) return;
    adminApi.get<EventDto>(`/api/admin/events/${id}`).then((data) => {
      const { id: _omit, ...rest } = data;
      setForm(rest);
      setLoaded(true);
    });
  }, [id, isNew]);

  function toggleCategory(categoryId: number) {
    setForm((f) => ({
      ...f,
      categoryIds: f.categoryIds.includes(categoryId)
        ? f.categoryIds.filter((c) => c !== categoryId)
        : [...f.categoryIds, categoryId],
    }));
  }

  async function handleSubmit() {
    setErrors([]);
    try {
      if (isNew) {
        await adminApi.post("/api/admin/events", form);
      } else {
        await adminApi.put(`/api/admin/events/${id}`, form);
      }
      navigate("/admin/karten");
    } catch (err) {
      if (err instanceof AdminApiError && err.body && typeof err.body === "object") {
        const body = err.body as { issues?: string[]; error?: string };
        setErrors(body.issues ?? [body.error ?? "Speichern fehlgeschlagen."]);
      } else {
        setErrors(["Speichern fehlgeschlagen."]);
      }
    }
  }

  if (!loaded) return <p>Lädt …</p>;

  return (
    <div>
      <h1 className={styles.pageTitle}>{isNew ? "Neue Karte" : `Karte #${id} bearbeiten`}</h1>

      {errors.length > 0 && (
        <div className={styles.errorBox}>
          <ul>
            {errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
      )}

      <div className={styles.card}>
        <div className={styles.formRow}>
          <label htmlFor="ev-year">Jahr (negativ = v. Chr.)</label>
          <input
            id="ev-year"
            type="number"
            className={styles.input}
            value={form.year}
            onChange={(e) => setForm({ ...form, year: Number(e.target.value) })}
          />
        </div>

        <div className={styles.formRow}>
          <label htmlFor="ev-fame">Bekanntheit</label>
          <select
            id="ev-fame"
            className={styles.select}
            value={form.fame}
            onChange={(e) => setForm({ ...form, fame: Number(e.target.value) as 1 | 2 | 3 })}
          >
            <option value={1}>1 — allgemein bekannt</option>
            <option value={2}>2</option>
            <option value={3}>3 — Spezialwissen</option>
          </select>
        </div>

        <div className={styles.formRow}>
          <label htmlFor="ev-text-de">Text (DE)</label>
          <textarea
            id="ev-text-de"
            className={styles.textarea}
            value={form.textDe}
            onChange={(e) => setForm({ ...form, textDe: e.target.value })}
          />
        </div>
        <div className={styles.formRow}>
          <label htmlFor="ev-text-en">Text (EN)</label>
          <textarea
            id="ev-text-en"
            className={styles.textarea}
            value={form.textEn}
            onChange={(e) => setForm({ ...form, textEn: e.target.value })}
          />
        </div>
        <div className={styles.formRow}>
          <label htmlFor="ev-info-de">Infotext (DE, optional)</label>
          <textarea
            id="ev-info-de"
            className={styles.textarea}
            value={form.infoDe ?? ""}
            onChange={(e) => setForm({ ...form, infoDe: e.target.value })}
          />
        </div>
        <div className={styles.formRow}>
          <label htmlFor="ev-info-en">Infotext (EN, optional)</label>
          <textarea
            id="ev-info-en"
            className={styles.textarea}
            value={form.infoEn ?? ""}
            onChange={(e) => setForm({ ...form, infoEn: e.target.value })}
          />
        </div>
        <div className={styles.formRow}>
          <label htmlFor="ev-source-label">Quelle — Name</label>
          <input
            id="ev-source-label"
            className={styles.input}
            value={form.sourceLabel}
            onChange={(e) => setForm({ ...form, sourceLabel: e.target.value })}
          />
        </div>
        <div className={styles.formRow}>
          <label htmlFor="ev-source-url">Quelle — URL</label>
          <input
            id="ev-source-url"
            className={styles.input}
            value={form.sourceUrl}
            onChange={(e) => setForm({ ...form, sourceUrl: e.target.value })}
          />
        </div>

        <div className={styles.formRow}>
          <span>Kategorien</span>
          <div className={styles.chipRow}>
            {categories.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`${styles.chip} ${form.categoryIds.includes(c.id) ? styles.chipActive : ""}`}
                onClick={() => toggleCategory(c.id)}
              >
                {c.nameDe}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.formRow}>
          <label htmlFor="ev-status">Status</label>
          <select
            id="ev-status"
            className={styles.select}
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value as "draft" | "published" })}
          >
            <option value="draft">Entwurf</option>
            <option value="published">Veröffentlicht</option>
          </select>
        </div>

        <div className={styles.formRow}>
          <label htmlFor="ev-note">Interne Notiz</label>
          <textarea
            id="ev-note"
            className={styles.textarea}
            value={form.note ?? ""}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
          />
        </div>

        <div className={styles.actions}>
          <button type="button" className={styles.button} onClick={handleSubmit}>
            Speichern
          </button>
        </div>
      </div>

      <div className={styles.card}>
        <h2>Live-Vorschau</h2>
        <CardPreview form={form} />
      </div>
    </div>
  );
}

function CardPreview({ form }: { form: FormState }) {
  return (
    <div>
      <p>
        <strong>{form.textDe || "(kein Text)"}</strong>
      </p>
      <p>{formatYear(form.year, "de")}</p>
      {form.infoDe && <p>{form.infoDe}</p>}
      {form.sourceLabel && (
        <p>
          Quelle: {form.sourceLabel} ({form.sourceUrl})
        </p>
      )}
    </div>
  );
}
